import { getJSONPref, removePref } from "./userPrefs";
import { tokenStore } from "@/lib/auth/tokenStore";
import { hasNonEmptyConditions } from "@/components/ConditionBuilder/condition-utils";
import type { TableFilter } from "@/types/TableFilter";
import type { TableView } from "@/types/TableView";

/**
 * Ad-hoc (unnamed, DEFAULT) view/filter snapshots.
 *
 * `ViewsProvider.activateView` / `FiltersProvider.activateFilter` write these
 * whenever the user APPLIES an unnamed setup — there is no server id to persist,
 * so the applied state is remembered locally instead. Reading them back is what
 * makes "Ansicht verwenden" survive a reload without a save. A NAMED view/filter
 * is already sticky through its stored id, and activating one deletes the
 * snapshot, so the two can never fight.
 *
 * Key shape mirrors `ViewsProvider.getCookieName` / `FiltersProvider.getCookieName`
 * exactly — `view-<surface>-<eventId>` plus the `-adhoc` suffix. Drift here is
 * silent (a snapshot that is written but never found), hence one shared builder.
 */
export type AdhocSurface =
  | "guest"
  | "registrant"
  | "companion"
  | "checkIn"
  | "emailSent"
  | "actionLog"
  | "contact"
  | "speaker"
  | "sponsor"
  // The root event list. NOT event-scoped — see `baseKey`.
  | "event";
// `eventLimits` is deliberately absent: `ViewsProvider`/`FiltersProvider` bail
// out for that type before any pref IO, so nothing is ever written for the
// subscription table and there is nothing to restore.

/**
 * The name this browser remembers a table's applied setup under.
 *
 * Mirrors `ViewsProvider.getCookieName` / `FiltersProvider.getCookieName` for
 * every event-scoped surface — drift there is silent (the writer stores under
 * one name and the reader looks under another), which is why both sides derive
 * it here.
 *
 * The ROOT event list is the exception, in BOTH directions. Its id pref is
 * `view-event` with no id at all, because the list is not event-scoped. But it
 * is COMPANY-scoped, and nothing clears prefs on a company switch: with a
 * company-blind cache key, a filter applied at ACME comes back on the dashboard
 * of the next company the same person opens — a list and a count dimmed by a
 * rule nobody there ever set. The stored id could never do that (a foreign id
 * fails to resolve and falls back to DEFAULT), and that very fallback is what
 * makes a DEFAULT-anchored cache entry match. So the cache key carries the
 * company, while the id pref is left exactly as it was.
 */
const scopedBase = (
  kind: "view" | "filter",
  surface: AdhocSurface,
  eventId: string,
) =>
  surface === "event"
    ? `${kind}-event-${tokenStore.getCompanyId() ?? ""}`
    : `${kind}-${surface}-${eventId}`;

export const tableCacheKey = (
  kind: "view" | "filter",
  surface: AdhocSurface,
  eventId: string,
) => `${scopedBase(kind, surface, eventId)}-adhoc`;

/**
 * "I chose the plain list" — a separate key, on purpose.
 *
 * Two different things wanted the one cache slot: the COLUMNS a user applied,
 * and the RECORD that they went back to the plain list instead of the view
 * their role points at (`roleSurfaceDefault`; activating DEFAULT deletes the id
 * pref, so nothing else remembers that choice). Keeping both in one entry meant
 * applying the plain list wiped the remembered columns of a named view — the
 * same silent loss the per-view rule had just closed, through the other door —
 * while dropping the record would bring the role's view back on the next load
 * and make the reset control look broken.
 *
 * They are also different in kind: one is a cache that may be thrown away at
 * any time, the other is a preference. Hence a preference key, which the
 * one-time cache reset deliberately does not touch.
 */
export const plainListKey = (surface: AdhocSurface, eventId: string) =>
  `${scopedBase("view", surface, eventId)}-plain`;

const viewKey = (surface: AdhocSurface, eventId: string) =>
  tableCacheKey("view", surface, eventId);
const filterKey = (surface: AdhocSurface, eventId: string) =>
  tableCacheKey("filter", surface, eventId);

/**
 * Drop a present-but-EMPTY `conditions` block ({AND, [], []}).
 *
 * "Present but empty" is not read as "no conditions" by every consumer: the URL
 * merge skips it (`hasNonEmptyConditions` is false) while the filter editor used
 * to render it verbatim. A dashboard click-through row then landed in the flat
 * `filter` array only — it narrowed the list and counted in the badge, but the
 * editor showed an empty condition builder, so the filter could not be seen or
 * removed. Ad-hoc snapshots written before `activateFilter` gained its
 * empty-guard still carry such a block, so heal them on read.
 */
export function stripEmptyConditions(filter: TableFilter): TableFilter {
  if (!filter.conditions || hasNonEmptyConditions(filter.conditions))
    return filter;
  const { conditions: _empty, ...rest } = filter;
  return rest as TableFilter;
}

/**
 * What the user last APPLIED on a table, as this browser remembers it.
 *
 * `baseViewId` says which view the columns were derived from; `baseUpdatedAt`
 * says which VERSION of it. Both are what keep a private convenience from
 * turning into a claim about the shared artifact.
 */
export type ViewCacheEntry = {
  baseViewId: string;
  /**
   * The columns the SERVER held when this was taken. The freshness check, and
   * deliberately not a timestamp: three save paths stamp `updatedAt` with a
   * client-side `new Date()` (`useViewHeader`, `useSaveView`, the unsaved-changes
   * guard) because the update endpoint returns nothing to read a real stamp
   * from. A locally minted stamp can never equal the server's, so a
   * timestamp-anchored entry died silently after the first save of that view —
   * the columns were simply gone on the next load, with no warning.
   *
   * Comparing the base columns is also the more honest question: what this
   * entry claims is "these are MY columns on top of THOSE". If somebody else
   * has since changed the shared list, the claim no longer holds; if they only
   * renamed the view or swapped its detail layout, it still does.
   */
  baseFields: string[];
  fields: string[];
};

const sameFields = (a?: string[], b?: string[]) =>
  JSON.stringify(a ?? []) === JSON.stringify(b ?? []);

/**
 * The columns a table should open with: the ones the user last applied, when
 * they still belong to the view that is active AND to the version of it that
 * the server currently holds.
 *
 * Three rules, and each exists because its absence produced a real defect:
 *
 *  - **Same view.** A cache entry from another view says nothing about this one;
 *    applying it would silently re-label somebody's columns.
 *  - **Same base.** The entry records the column list the server held when it
 *    was taken. If that no longer matches, somebody else has edited the shared
 *    view since, the entry is stale and is DROPPED, not overlaid. Silently
 *    winning over someone else's saved work is the worse failure — and the app
 *    would then offer to write the stale list back over theirs.
 *  - **Usable shape.** A missing or empty column list yields a table with no
 *    columns, which renders `invisible` with the pagination still showing. An
 *    unusable entry is no entry.
 *
 * Metadata (name, description, linked detail layout) always comes from the
 * server copy — only the column list is the user's.
 *
 * Call this on every render, NOT once at mount: pages unmount their whole
 * provider subtree while a changed filter re-queries and re-seed from this
 * value. A snapshot frozen at mount resurrected the ad-hoc filter the user had
 * just cleared. Identity churn is harmless — both values only seed provider
 * state and content-stable query keys.
 */
export function applyAdhocView(
  active: TableView,
  surface: AdhocSurface,
  eventId: string,
): TableView {
  const entry = getJSONPref<ViewCacheEntry | null>(
    viewKey(surface, eventId),
    null,
  );
  if (!entry || typeof entry !== "object") return active;
  if (!Array.isArray(entry.fields) || entry.fields.length === 0) return active;
  if (entry.baseViewId !== active?.tableViewId) return active;
  if (!Array.isArray(entry.baseFields)) return active;
  if (!sameFields(entry.baseFields, active?.fields)) return active;
  return { ...active, fields: entry.fields };
}

/**
 * What `applyAdhocView` WOULD restore next time — the baseline "is there
 * anything at risk here?" has to be measured against.
 *
 * The unsaved-changes guard used to compare the active state with the SERVER
 * copy, which was right while nothing survived a reload. With the cache it is
 * wrong: a restored view differs from the server copy by design, so the guard
 * declared unsaved work at first paint, blocked every navigation, and offered a
 * Save that writes to the SHARED view. Measuring against this instead means
 * "dirty" recovers its literal meaning — you changed something that would
 * otherwise be lost — and it self-heals: where the cache cannot be written
 * (private mode, quota), this returns the server copy and the guard warns again,
 * correctly.
 */
export function wouldRestoreView(
  serverView: TableView,
  surface: AdhocSurface,
  eventId: string,
): TableView {
  return applyAdhocView(serverView, surface, eventId);
}

/**
 * Forget the remembered columns OF ONE VIEW.
 *
 * The surface has a single slot, so a blind `removePref` on this key throws
 * away whatever happens to be in it — which is not always the view being acted
 * on. Saving view B while view A is active and tweaked used to delete A's
 * memory; so did activating an unchanged B. Both are silent losses of work the
 * user had applied, so the id has to be checked before the slot is cleared.
 *
 * A missing `viewId` clears nothing. It used to mean "whatever is in there",
 * which reopened the very hole this function closes: a view object without an
 * id (a `Promise.allSettled` fallback, a malformed server answer) would have
 * emptied the slot of whichever OTHER view was remembered. A view with no id
 * has no entry of its own anyway, so there is nothing to lose by refusing.
 */
export function clearViewCache(
  surface: AdhocSurface,
  eventId: string,
  viewId?: string,
): void {
  if (viewId === undefined) return;
  const key = viewKey(surface, eventId);
  const entry = getJSONPref<ViewCacheEntry | null>(key, null);
  if (!entry || typeof entry !== "object") return;
  if (entry.baseViewId !== viewId) return;
  removePref(key);
}

/** Filter counterpart of {@link applyAdhocView}; heals legacy empty groups. */
export function applyAdhocFilter(
  active: TableFilter,
  surface: AdhocSurface,
  eventId: string,
): TableFilter {
  if (active?.tableFilterId !== "DEFAULT") return active;
  const snapshot = getJSONPref<TableFilter | null>(
    filterKey(surface, eventId),
    null,
  );
  if (!snapshot || typeof snapshot !== "object") return active;
  // Same shape guard the view side has, for the same reason and one sharper:
  // `JSON.stringify` drops undefined keys, and two consumers spread
  // `activeFilter.filter` without a fallback (`DashboardRootPage`,
  // `EventMessagingSentMailsPage`), so an entry without that array is not a
  // narrowed list — it is a white page.
  if (snapshot.filter !== undefined && !Array.isArray(snapshot.filter))
    return active;
  if (!Array.isArray(snapshot.filter) && !snapshot.conditions) return active;
  return stripEmptyConditions(
    Array.isArray(snapshot.filter) ? snapshot : { ...snapshot, filter: [] },
  );
}
